import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  AppRemixIcon,
  AppNoInfo,
  AppNotificationAlert,
} from '@/layout/AppComponents';
import { UnmountClosed } from 'react-collapse/lib/UnmountClosed';
import LazyLoad from 'react-lazyload';
import ReactDOMServer from 'react-dom/server';
import * as moduleAction from '../../../store/actions/moduleAction';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import EvaluationSidebar from '../../../layout/screenSpecifics/Modules/LegalCompliance/Evaluation/Sidebar/EvaluationSidebar';
import { axiosPath, path } from '../../../routing/helpers/router';
import PlantObligationContainer from '../../../layout/screenSpecifics/Modules/LegalCompliance/Evaluation/PlantObligationContainer';
import GCLPaginator from '../../../layout/screenSpecifics/Modules/LegalCompliance/Evaluation/GCLPaginator';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import BlockChangeOptions from '../../../layout/screenSpecifics/Modules/LegalCompliance/Evaluation/BlockChangeOptions';
import Translator from '../../../layout/components/helpers/Translator';
import GCLStages from '../../../layout/screenSpecifics/Modules/LegalCompliance/Evaluation/GCLStages';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import { copyToClibpoard } from '../../../minions/string';
import _ from 'lodash';

const LegalComplianceEvaluationScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { moduleBridgeHex, plantHex, filtersHex, page } = useParams();
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex]?.permissions,
  );
  const isSidebarOpened = useSelector(
    (state) => state.auth?.settings?.preferences?.sidebar?.isOpened,
  );
  const obligationsCheckbox = useSelector(
    (state) => state.currentPage?.options?.obligationsCheckbox,
  );
  const poLevel1 = useSelector(
    (state) => state.currentPage?.plantObligationLevels?.level1,
  );
  const poLevel2 = useSelector(
    (state) => state.currentPage?.plantObligationLevels?.level2,
  );
  const poLevel3 = useSelector(
    (state) => state.currentPage?.plantObligationLevels?.level3,
  );
  const plantObligationPages = useSelector(
    (state) => state.currentPage?.gcl?.plantObligations,
  );
  const visiblePlantObligations = useSelector(
    (state) => state.currentPage?.visiblePlantObligations,
  );
  const userMark = useSelector((state) => state.currentPage?.options?.userMark);
  const goToMark = useSelector((state) => state.currentPage?.goToMark);
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState(null);
  const [plantObligations, setPlantObligations] = useState(null);

  useEffect(() => {
    dispatch(currentPageAction.change('plantObligationLevels', {}, true));
    dispatch(currentPageAction.change('checkedPlantObligations', [], true));
  }, [filtersHex]);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  useEffect(() => {
    if (
      plantObligations &&
      plantObligations?.plantObligationsIDs?.[page - 1] === undefined
    ) {
      navigate(
        path({
          name: 'user_module_legalCompliance_evaluation',
          params: {
            moduleBridgeHex,
            plantHex,
            page: 1,
            filtersHex,
          },
        }),
      );
    }
  }, [plantObligations]);

  // > LOAD LAYOUT
  const loadLayout = async () => {
    await axios
      .post(axiosPath('api_r_user_module_legalCompliance_evaluation_loadLayout'), {
        moduleBridgeHex,
        plantHex,
      })
      .then(({ data }) => {
        setLayout(data);
        dispatch(currentPageAction.change('options', data.options));
      })
      .catch(() => {
        navigate(
          path({
            name: 'user_home',
          }),
        );
      });
  };

  useEffect(() => {
    if (moduleBridgeHex && plantHex) {
      loadLayout();
    }
  }, [moduleBridgeHex, plantHex]);
  // < LOAD LAYOUT

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_legalCompliance_evaluation_getPermissions'),
          'permissions',
          {
            moduleBridgeHex,
            plantHex,
            subType: 'permissions',
          },
        ),
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_legalCompliance_evaluation_getPermissions'),
          'userBridge',
          {
            moduleBridgeHex,
            plantHex,
            subType: 'userBridge',
          },
        ),
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_legalCompliance_evaluation_getPermissions'),
          'jsonSettings',
          {
            moduleBridgeHex,
            plantHex,
            subType: 'jsonSettings',
          },
        ),
      );
    } catch (err) {}
  }, [moduleBridgeHex, plantHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex, plantHex]);
  // < STORE CALLS

  // > LOAD PLANTOBLIGATIONS
  const loadPlantObligations = async () => {
    await axios
      .post(
        axiosPath(
          'api_r_user_module_legalCompliance_evaluation_loadPlantObligations',
        ),
        {
          moduleBridgeHex,
          plantHex,
          filtersHex,
        },
      )
      .then(({ data }) => {
        if (isMounted.current) setPlantObligations(data);
        dispatch(
          currentPageAction.change('gcl', {
            plantObligations: data.plantObligationsIDs,
          }),
        );
        dispatch(currentPageAction.change('pagination', data.pagination));
        dispatch(
          currentPageAction.change('plantObligationsPage', data.plantObligationsIDs),
        );
      });
  };
  useEffect(() => {
    if (layout) {
      setLoading(true);
      loadPlantObligations();
    }
  }, [layout, filtersHex]);
  // < LOAD PLANTOBLIGATIONS

  // > LOAD PLANTOBLIGATION LEVELS
  const loadPoLevel = async (level) => {
    await axios
      .post(axiosPath('api_r_user_module_legalCompliance_evaluation_loadPoLevel'), {
        moduleBridgeHex,
        plantHex,
        poIDs: plantObligations.plantObligationsIDs?.[page - 1],
        level,
      })
      .then(({ data }) => {
        if (level == 1) {
          dispatch(
            currentPageAction.change(
              'plantObligationLevels',
              { level1: !poLevel1 ? data : { ...poLevel1, ...data } },
              true,
            ),
          );

          loadPoLevel(2);
        }

        if (level == 2) {
          dispatch(
            currentPageAction.change('plantObligationLevels', {
              level2: !poLevel2 ? data : { ...poLevel2, ...data },
            }),
          );

          loadPoLevel(3);
        }

        if (level == 3) {
          dispatch(
            currentPageAction.change('plantObligationLevels', {
              level3: !poLevel3 ? data : { ...poLevel3, ...data },
            }),
          );
        }
      });
  };

  useEffect(() => {
    if (plantObligations?.plantObligationsIDs) {
      setLoading(false);
      window.scrollTo(0, 0);
      if (
        plantObligations?.plantObligationsIDs.length > 0 &&
        !poLevel3?.[plantObligationPages?.[page - 1]?.[0]]
      )
        loadPoLevel(1);
    }
  }, [plantObligations, page]);
  // < LOAD PLANTOBLIGATIONS LEVELS

  // > GO TO MARKER
  useEffect(() => {
    if (
      _.filter(plantObligationPages, (p) => p.includes(userMark)).length > 0 &&
      goToMark
    ) {
      dispatch(currentPageAction.change('goToMark', false, true));
      const elementPosition = _.indexOf(plantObligationPages[page - 1], userMark);
      window.scrollTo({
        top: 168 * elementPosition + 70,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [userMark, goToMark]);
  // < GO TO MARKER

  const copyObligations = useCallback(
    async (type) => {
      await axios
        .post(
          axiosPath('api_r_user_module_legalCompliance_evaluation_copyObligations'),
          {
            moduleBridgeHex,
            plantHex,
            filtersHex,
          },
        )
        .then(({ data }) => {
          copyToClibpoard(type === 'ids' ? data.ids.join(',') : data.codes.join(','));
          AppNotificationAlert({
            title: ReactDOMServer.renderToString(
              <Translator file="module-communicationPipeline" name="copied" />,
            ),
            message: ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="module.lc.copied-clipboard"
              />,
            ),
            type: 'success',
            duration: 5000,
            position: 'top-right',
          });
        })
        .catch(() => {
          AppNotificationAlert({
            title: ReactDOMServer.renderToString(
              <Translator file="module-communicationPipeline" name="copied" />,
            ),
            message: ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="no.obligations" />,
            ),
            type: 'success',
            duration: 5000,
            position: 'top-right',
          });
          return;
        });
    },
    [moduleBridgeHex, plantHex, filtersHex],
  );

  return (
    <>
      <EvaluationSidebar />
      <BreadcrumbHandler
        paths={
          layout
            ? [
                {
                  name: layout.plant.name,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: `${ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="evaluation" />,
                  )} (${
                    plantObligations?.pagination?.totalCount || '--'
                  } ${ReactDOMServer.renderToString(
                    <Translator file="module-intelligence" name="obligations" />,
                  )})`,
                },
              ]
            : [{ name: '...' }]
        }
      />

      <main role="main" className="col-md-12 col-lg-12 px-md-4">
        <div className={`${isSidebarOpened && 'sidebar-expanded'}`}>
          <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0 border-bottom">
            {permissions?.gcl?.module?.plant?.stageGates && <GCLStages />}
            <section className="content mb-5">
              {filtersHex !== '0' && (
                <div className="alert alert-info pointer">
                  <span
                    onClick={() =>
                      navigate(
                        path({
                          name: 'user_module_legalCompliance_evaluation',
                          params: {
                            moduleBridgeHex,
                            plantHex,
                            page: 1,
                            filtersHex: '0',
                          },
                        }),
                      )
                    }
                  >
                    <AppRemixIcon icon="delete-bin-6" className="mr-2" />
                    <Translator file="filter" name="filter.delete" />
                  </span>
                  <span
                    className="ml-4"
                    onClick={() =>
                      dispatch(
                        currentPageAction.change('auxSidebar', {
                          opened: 'filters',
                        }),
                      )
                    }
                  >
                    <AppRemixIcon icon="equalizer" className="mr-2" />
                    <Translator file="module-legalCompliance" name="show.filters" />
                  </span>
                  <span className="ml-4" onClick={() => copyObligations('ids')}>
                    <AppRemixIcon
                      icon="sort-number-asc"
                      noVariants
                      className="mr-2"
                    />
                    <Translator
                      file="module-legalCompliance"
                      name="module.lc.copy-ids"
                    />
                  </span>
                  <span className="ml-4" onClick={() => copyObligations('codes')}>
                    <AppRemixIcon icon="barcode" className="mr-2" />
                    <Translator
                      file="module-legalCompliance"
                      name="module.lc.copy-codes"
                    />
                  </span>
                </div>
              )}
              <UnmountClosed isOpened={obligationsCheckbox}>
                <BlockChangeOptions />
              </UnmountClosed>
              {loading ? (
                <AppNoInfo minHeight={window.innerHeight - 60} />
              ) : layout &&
                plantObligations.plantObligationsIDs &&
                plantObligations.plantObligationsIDs.length > 0 &&
                (!visiblePlantObligations || visiblePlantObligations?.length > 0) ? (
                (visiblePlantObligations && visiblePlantObligations.length > 0
                  ? visiblePlantObligations
                  : plantObligations.plantObligationsIDs?.[page - 1]
                )?.map((poID) => (
                  <LazyLoad key={poID} height={168}>
                    <PlantObligationContainer type="aa" plantObligationID={poID} />
                  </LazyLoad>
                ))
              ) : (
                <div className="alert alert-info">
                  <Translator file="module-legalCompliance" name="no.obligations" />
                </div>
              )}
              {!permissions?.gcl?.module?.plant?.disablePaginator &&
                plantObligations?.plantObligationsIDs &&
                plantObligations.plantObligationsIDs.length > 0 &&
                plantObligations?.plantObligationsIDs?.[page - 1] !== undefined && (
                  <GCLPaginator
                    pagination={plantObligations.pagination}
                    route="user_module_legalCompliance_evaluation"
                    page={page}
                    plantObligationPages={plantObligations.plantObligationsIDs}
                    itemsPerPage={20}
                    className="mt-3"
                    params={{
                      moduleBridgeHex,
                      plantHex,
                      page,
                      filtersHex,
                    }}
                  />
                )}
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default LegalComplianceEvaluationScreen;
